import Tabulator, { CellComponent, ColumnComponent, ColumnDefinition, Options, RowComponent } from 'tabulator-tables';
import { MassiaTabulatorColumnsBuilder } from '../builders/massia.tabulator.columns.builder';
import { MassiaTabulatorOptionsBuilder, MassiaTabulatorOptionsContext } from '../builders/massia.tabulator.options.builder';
import { MassiaTabulatorColumnDef, MassiaTabulatorGridConfig } from '../massia.tabulator.type';

/**
 * Encapsule la logique bas niveau de la manipulation de la librairie native Tabulator.
 * S'assure que le contrôleur AngularJS reste abstrait.
 */
export class MassiaTabulatorManager {
	public tabulatorInstance: Tabulator | null = null;
	public tableReady = false;

	private counterEl: HTMLElement | null = null;
	private lastRemoteTotal: number = 0;
	private columnUpdateQueue: MassiaTabulatorColumnDef[] | null = null;
	private pendingData: unknown[] | null = null;
	private _originalColumns: MassiaTabulatorColumnDef[] = [];
	private _tabulatorListeners: Array<{ event: string; fn: (...args: unknown[]) => void }> = [];

	constructor(
		private $scope: ng.IScope,
		private notification: { success: (m: string) => void; error: (m: string) => void },
		private onRowSelectionChanged: (data: unknown[], rows: RowComponent[]) => void,
		private onCellEditedCb: (event: { $event: { row: unknown; field: string; value: unknown } }) => void
	) {}

	/**
	 * Initialise l'instance Tabulator native sur l'élément du DOM spécifié
	 */
	public init(
		tableElement: HTMLElement,
		gridConfig: MassiaTabulatorGridConfig,
		dataset: unknown[],
		optionsOverrides?: Partial<Options>,
		searchQuery: string = ''
	): void {
		if (this.tabulatorInstance) return;

		// Préparation des colonnes
		this._originalColumns = gridConfig.columns || [];
		const columns = MassiaTabulatorColumnsBuilder.build(this._originalColumns, gridConfig.features || {}, searchQuery);

		// Construction contexte d'options
		const ctx: MassiaTabulatorOptionsContext = {
			gridConfig,
			dataset,
			optionsOverrides,
			notification: this.notification,
			captureRemoteTotal: (t) => (this.lastRemoteTotal = t),
			getCounterEl: () => this.counterEl
		};

		// Construction Config Native
		const rawOptions = MassiaTabulatorOptionsBuilder.build(ctx, columns);

		// Instantiation Native !
		this.tabulatorInstance = new Tabulator(tableElement, rawOptions);

		// Attachement des Listeners Natifs Tabulator
		this.attachListeners(gridConfig);
	}

	private on(event: string, fn: (...args: unknown[]) => void): void {
		this.tabulatorInstance!.on(event as Parameters<typeof this.tabulatorInstance.on>[0], fn as never);
		this._tabulatorListeners.push({ event, fn });
	}

	private attachListeners(gridConfig: MassiaTabulatorGridConfig): void {
		if (!this.tabulatorInstance) return;

		// Listener: Mise à jour du compteur de pagination
		const updateCounter = () => {
			if (this.counterEl && this.tabulatorInstance) {
				const max = this.tabulatorInstance.getPageMax() || 1;
				const current = this.tabulatorInstance.getPage() || 1;
				const total = this.lastRemoteTotal;
				this.counterEl.innerText = `${current} / ${max} pages (${total} élément${total > 1 ? 's' : ''})`;

				const parent = this.counterEl.parentElement;
				if (parent && parent.lastElementChild !== this.counterEl) {
					parent.appendChild(this.counterEl);
				}
			}
		};

		this.on('pageLoaded', updateCounter);
		this.on('dataLoaded', updateCounter);

		// Listener: Construction de table terminée
		this.on('tableBuilt', () => {
			this.tableReady = true;

			// Récupération de l'élément div counter injecté par config builder
			const remoteFeature = gridConfig.features?.remote;
			if (remoteFeature?.element) {
				const el =
					typeof remoteFeature.element === 'string'
						? (document.querySelector(remoteFeature.element as string) as HTMLElement)
						: (remoteFeature.element as HTMLElement);
				if (el) {
					this.counterEl = el.querySelector('.massia-page-counter') as HTMLElement;
				}
			}

			// Pile de mise à jour colonnes si appelé avant `tableBuilt`
			if (this.columnUpdateQueue) {
				this.setColumns(this.columnUpdateQueue);
				this.columnUpdateQueue = null;
			}

			// Données en attente (local uniquement)
			if (this.pendingData) {
				const activeSelectAlls = (gridConfig.columns || []).filter((c) => c._selectAllState?.active).map((c) => c._selectAllState!);
				if (activeSelectAlls.length > 0) {
					(this.pendingData as Record<string, unknown>[]).forEach((row) => {
						activeSelectAlls.forEach((state) => {
							if (!state.enabledWhenField || row[state.enabledWhenField]) {
								row[state.field] = true;
							}
						});
					});
				}
				this.tabulatorInstance!.setData(this.pendingData as Record<string, unknown>[]);
				this.pendingData = null;
			}
			updateCounter();
		});

		// Listener: Clic simple
		this.on('rowClick', (e: UIEvent, row: RowComponent) => {
			// Sauvegarde de l'état de la grille si la persistance est activée avant de naviguer
			const persistence = gridConfig?.features?.persistence;
			if (persistence?.onSaveGridState && this.tabulatorInstance) {
				persistence.onSaveGridState(() => ({
					page: this.tabulatorInstance!.getPage() as number,
					size: this.tabulatorInstance!.getPageSize() as number,
					sort: this.tabulatorInstance!.getSorters() as unknown[],
					filter: this.tabulatorInstance!.getHeaderFilters() as { field: string; value: unknown }[]
				}));
			}

			if (gridConfig?.events?.onRowClick) {
				gridConfig.events.onRowClick(e, row.getData());
			}
		});

		// Listener: Changement sélection
		this.on('rowSelectionChanged', (data: unknown[], rows: RowComponent[]) => {
			this.$scope.$applyAsync(() => {
				this.onRowSelectionChanged(data, rows);
			});
		});

		// Listener: Cellule éditée
		this.on('cellEdited', (cell: CellComponent) => {
			cell.getElement().classList.add('cell-modified');
			if (this.onCellEditedCb) {
				const row = cell.getRow().getData();
				const field = cell.getField();
				const value = cell.getValue();
				this.$scope.$applyAsync(() => {
					this.onCellEditedCb({ $event: { row, field, value } });
				});
			}
		});
	}

	public getColumns(): ColumnComponent[] {
		return this.tabulatorInstance?.getColumns() || [];
	}

	public setColumns(columns: MassiaTabulatorColumnDef[]): void {
		if (this.tabulatorInstance && this.tableReady) {
			const cleanColumns = columns.map((c) => {
				const clean = { ...c } as MassiaTabulatorColumnDef;
				delete clean._selectAllState;
				return clean as ColumnDefinition;
			});
			this.tabulatorInstance.setColumns(cleanColumns);
		} else {
			this.columnUpdateQueue = columns;
		}
	}

	/**
	 * Destructeur propre
	 */
	public destroy(): void {
		if (this.tabulatorInstance) {
			this._tabulatorListeners.forEach(({ event, fn }) =>
				this.tabulatorInstance!.off(event as Parameters<typeof this.tabulatorInstance.off>[0], fn as never)
			);
			this._tabulatorListeners = [];
			this.tabulatorInstance.destroy();
			this.tabulatorInstance = null;
		}
	}

	/**
	 * Recharge les données dans la grille actuelle
	 */
	public setData(isRemote: boolean, remoteAjaxUrl?: string): void {
		if (!this.tabulatorInstance) return;

		// Réinitialisation des checkboxes select-all via les références originales (getDefinition retourne une copie)
		this._originalColumns.forEach((col) => {
			if (col._selectAllState?.active) {
				col._selectAllState.active = false;
			}
		});

		if (isRemote && remoteAjaxUrl) {
			this.tabulatorInstance.setData(remoteAjaxUrl);
		} else {
			this.tabulatorInstance.setData();
		}
	}

	/**
	 * Met à jour dynamiquement les données locales sans détruire l'instance
	 */
	public setLocalData(data: Record<string, unknown>[]): void {
		if (!this.tabulatorInstance) return;
		if (!this.tableReady) {
			this.pendingData = data;
			return;
		}
		this.tabulatorInstance.setData(data);
	}
}
